/**
 * TradeCard - Displays a trade proposal in a clean, balanced card format
 *
 * Shows both sides of the trade with larger NFT previews,
 * clear status indication, and intuitive action buttons.
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FeatherClock, FeatherMessageCircle } from '@subframe/core';
import { is3DModelUrl } from '../../utils/media';
import type { NFTTrade, TradeItem } from '../../api/trades';
import { formatAddress } from '../../utils/formatters';
import { dropsToXrp } from '../../utils/currency';
import MultiCollectionWarning from './MultiCollectionWarning';

interface TradeCardProps {
  trade: NFTTrade;
  currentUserAddress: string;
  onAccept?: (tradeId: string) => void;
  onDecline?: (tradeId: string) => void;
  onCancel?: (tradeId: string) => void;
  onCounter?: (tradeId: string) => void;
  onClick?: (tradeId: string) => void;
}

const STATUS_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  proposed: { bg: 'bg-brand-50', text: 'text-brand-600', label: 'Pending' },
  countered: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Countered' },
  accepted: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Accepted' },
  signing: { bg: 'bg-purple-50', text: 'text-purple-600', label: 'Signing' },
  settling: { bg: 'bg-purple-50', text: 'text-purple-600', label: 'Settling' },
  completed: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Completed' },
  declined: { bg: 'bg-neutral-100', text: 'text-subtext-color', label: 'Declined' },
  expired: { bg: 'bg-neutral-100', text: 'text-subtext-color', label: 'Expired' },
  cancelled: { bg: 'bg-red-50', text: 'text-red-600', label: 'Cancelled' },
};

function formatTimeRemaining(expiresAt: string | null | undefined): string {
  if (!expiresAt) return '';
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) return `${days}d ${hours}h left`;
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}

function NFTStack({ items, xrpDrops }: { items: TradeItem[]; xrpDrops: string }) {
  const xrpAmount = dropsToXrp(xrpDrops);
  const hasNFTs = items.length > 0;
  const hasXRP = xrpAmount > 0;

  if (!hasNFTs && !hasXRP) {
    return (
      <div className="flex items-center justify-center h-20 text-body text-subtext-color italic">
        Nothing
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      {/* NFT Grid - show up to 4 in a row */}
      {hasNFTs && (
        <div className="flex flex-wrap justify-center gap-2">
          {items.slice(0, 4).map((item) => (
            <Link
              key={item.id}
              to={`/nfts/${item.nft_token_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="relative group"
            >
              <div className="w-16 h-16 rounded-md bg-neutral-100 border border-neutral-border overflow-hidden hover:border-brand-300 hover:shadow-md transition-all">
                {is3DModelUrl(item.nft_thumbnail_url || item.nft_image_url) ? (
                  <div className="w-full h-full bg-gradient-to-br from-neutral-100 to-neutral-200 flex items-center justify-center">
                    <span className="text-lg">🧊</span>
                  </div>
                ) : item.nft_thumbnail_url || item.nft_image_url ? (
                  <img
                    src={item.nft_thumbnail_url || item.nft_image_url || ''}
                    alt={item.nft_name || 'NFT'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-caption text-subtext-color">
                    NFT
                  </div>
                )}
              </div>
            </Link>
          ))}
          {items.length > 4 && (
            <div className="w-16 h-16 rounded-md bg-neutral-100 border border-neutral-border flex items-center justify-center">
              <span className="text-body-bold text-subtext-color">+{items.length - 4}</span>
            </div>
          )}
        </div>
      )}

      {/* NFT names */}
      {hasNFTs && (
        <div className="text-caption text-subtext-color text-center truncate max-w-full">
          {items.slice(0, 2).map((i) => i.nft_name || 'Unnamed').join(', ')}
          {items.length > 2 && ` +${items.length - 2} more`}
        </div>
      )}

      {/* XRP amount */}
      {hasXRP && (
        <div className={`text-body-bold text-brand-600 ${hasNFTs ? '' : 'mt-2'}`}>
          {hasNFTs && '+ '}{xrpAmount.toLocaleString()} XRP
        </div>
      )}
    </div>
  );
}

export default function TradeCard({
  trade,
  currentUserAddress,
  onAccept,
  onDecline,
  onCancel,
  onCounter,
  onClick,
}: TradeCardProps) {
  const navigate = useNavigate();
  const isInitiator = trade.initiator_address === currentUserAddress;
  const isRecipient = trade.recipient_address === currentUserAddress;
  const isActiveParty =
    (trade.active_party === 'initiator' && isInitiator) ||
    (trade.active_party === 'recipient' && isRecipient);

  const otherAddress = isInitiator ? trade.recipient_address : trade.initiator_address;
  const isPending = ['proposed', 'countered'].includes(trade.status);
  const isTerminal = ['completed', 'declined', 'expired', 'cancelled'].includes(trade.status);

  // For display: "They give" / "You give" from the current user's perspective
  const theyGiveItems = isInitiator ? trade.recipient_items : trade.initiator_items;
  const youGiveItems = isInitiator ? trade.initiator_items : trade.recipient_items;
  const theyGiveXrp = isInitiator ? trade.recipient_xrp_drops : trade.initiator_xrp_drops;
  const youGiveXrp = isInitiator ? trade.initiator_xrp_drops : trade.recipient_xrp_drops;

  const statusConfig = STATUS_CONFIG[trade.status] || STATUS_CONFIG.proposed;

  // Determine if the card should show signing action (which makes clicking meaningful)
  const showSigningAction =
    trade.status === 'accepted' || trade.status === 'signing';

  const handleMessageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/inbox?start=${otherAddress}`);
  };

  return (
    <div className="border border-solid border-neutral-border rounded-md bg-default-background overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-solid border-neutral-border">
        <div className="flex items-center gap-3">
          <span className={`text-caption font-medium px-2 py-0.5 rounded ${statusConfig.bg} ${statusConfig.text}`}>
            {statusConfig.label}
          </span>
          {/* Your turn / Waiting indicator for active trades */}
          {isPending && (
            isActiveParty ? (
              <span className="text-caption font-medium text-brand-600">
                Your turn
              </span>
            ) : (
              <span className="text-caption text-subtext-color">
                Waiting on them
              </span>
            )
          )}
          <div className="flex items-center gap-2">
            <span className="text-body text-subtext-color">
              {isInitiator ? 'To' : 'From'}
            </span>
            <Link
              to={`/wallets/${otherAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-body text-default-font hover:text-brand-600 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {formatAddress(otherAddress)}
            </Link>
            <button
              onClick={handleMessageClick}
              className="p-1 rounded hover:bg-neutral-100 transition-colors"
              title="Send message"
            >
              <FeatherMessageCircle className="h-4 w-4 text-subtext-color hover:text-brand-600" />
            </button>
          </div>
        </div>
        {trade.expires_at && !isTerminal && (
          <div className="flex items-center gap-1.5 text-caption text-subtext-color">
            <FeatherClock className="h-3.5 w-3.5" />
            {formatTimeRemaining(trade.expires_at)}
          </div>
        )}
      </div>

      {/* Trade Content */}
      <div className="p-6">
        <div className="flex items-center gap-6">
          {/* You Receive */}
          <div className="flex-1 min-w-0">
            <div className="text-caption font-medium text-emerald-600 uppercase tracking-wide mb-3 text-center">
              You Receive
            </div>
            <NFTStack items={theyGiveItems} xrpDrops={theyGiveXrp} />
          </div>

          {/* Arrow - simple SVG */}
          <div className="flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-subtext-color">
              <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          {/* You Send */}
          <div className="flex-1 min-w-0">
            <div className="text-caption font-medium text-amber-600 uppercase tracking-wide mb-3 text-center">
              You Send
            </div>
            <NFTStack items={youGiveItems} xrpDrops={youGiveXrp} />
          </div>
        </div>

        {/* Message */}
        {trade.message && (
          <div className="mt-4 px-3 py-2 bg-neutral-50 rounded border border-neutral-border">
            <p className="text-body text-default-font italic">"{trade.message}"</p>
          </div>
        )}

        {/* Multi-collection warning */}
        <MultiCollectionWarning
          sideA={trade.initiator_items}
          sideB={trade.recipient_items}
          compact
          className="mt-4"
        />
      </div>

      {/* Footer - only show if there are actions or terminal status */}
      {(isPending || showSigningAction || isTerminal) && (
        <div className="px-4 py-3 border-t border-solid border-neutral-border">
          <div className="flex items-center justify-end gap-2">
            {/* Status text - only for specific states that need explanation */}
            {trade.status === 'settling' && (
              <span className="text-caption text-subtext-color mr-auto">Processing trade...</span>
            )}
            {isTerminal && (
              <span className="text-caption text-subtext-color mr-auto">{new Date(trade.updated_at).toLocaleDateString()}</span>
            )}

            {/* Active party actions - order: Decline, Counter, Accept */}
            {isPending && isActiveParty && (
              <>
                {onDecline && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDecline(trade.id);
                    }}
                    className="px-4 py-1.5 text-red-600 text-body font-medium rounded hover:bg-red-50 transition-colors"
                  >
                    Decline
                  </button>
                )}
                {onCounter && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCounter(trade.id);
                    }}
                    className="px-4 py-1.5 bg-neutral-100 text-default-font text-body font-medium rounded hover:bg-neutral-200 transition-colors"
                  >
                    Counter
                  </button>
                )}
                {onAccept && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAccept(trade.id);
                    }}
                    className="px-4 py-1.5 bg-brand-600 text-white text-body font-medium rounded hover:bg-brand-700 transition-colors"
                  >
                    Accept
                  </button>
                )}
              </>
            )}

            {/* Waiting party cancel */}
            {isPending && !isActiveParty && onCancel && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel(trade.id);
                }}
                className="px-4 py-1.5 text-red-600 text-body font-medium rounded hover:bg-red-50 transition-colors"
              >
                Cancel
              </button>
            )}

            {/* Signing action - either party can sign */}
            {(trade.status === 'accepted' || trade.status === 'signing') && (() => {
              const mySideCompleted = isInitiator
                ? trade.initiator_signing_completed_at
                : trade.recipient_signing_completed_at;

              if (mySideCompleted) {
                return (
                  <span className="text-caption text-subtext-color mr-auto">
                    Waiting for other party to sign...
                  </span>
                );
              }

              return (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick?.(trade.id);
                  }}
                  className="px-4 py-1.5 bg-brand-600 text-white text-body font-medium rounded hover:bg-brand-700 transition-colors"
                >
                  Sign Your Side
                </button>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
